import { z } from 'zod';
import { auditFields, shortText, ulidSchema } from '../common.js';

/**
 * A contract or an invoice, kept in S3 and pointed at from here.
 *
 * The **S3 key is never supplied by the client**. It is derived server-side from
 * the wedding and the document id, so one wedding cannot be made to write into
 * another's prefix by asking nicely.
 */

/** What a browser can upload and what we are willing to hand back. */
export const DOCUMENT_TYPES = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/heic',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
] as const;
export type DocumentType = (typeof DOCUMENT_TYPES)[number];

/** 25MB. A contract is a few pages; anything larger is a mistake or a video. */
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;

export const documentSchema = z.object({
  type: z.literal('Document'),
  wedId: ulidSchema,
  docId: ulidSchema,
  /** As the person's filesystem had it, for showing back. Never used as a key. */
  filename: shortText,
  contentType: z.enum(DOCUMENT_TYPES),
  sizeBytes: z.number().int().min(0).max(MAX_DOCUMENT_BYTES),
  /** Where it hangs, if anywhere. A document can also just belong to the wedding. */
  vendorId: ulidSchema.optional(),
  venueId: ulidSchema.optional(),
  /**
   * Set once the upload is known to have landed. A row without it is a presign
   * nobody completed — listed separately rather than counted as a document.
   */
  uploadedAt: z.string().datetime().optional(),
  ...auditFields,
});
export type DocumentMeta = z.infer<typeof documentSchema>;

export const presignUploadSchema = z.object({
  filename: shortText,
  contentType: z.enum(DOCUMENT_TYPES),
  /**
   * Declared up front so the signature can pin it. The signed URL then only
   * accepts exactly this many bytes, which is what stops a 25MB limit being a
   * suggestion.
   */
  sizeBytes: z.number().int().min(1).max(MAX_DOCUMENT_BYTES),
  vendorId: ulidSchema.optional(),
  venueId: ulidSchema.optional(),
});
export type PresignUploadInput = z.infer<typeof presignUploadSchema>;
